import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useMovable } from '../nook/useMovable';
import { hangBeside } from './hangPlace';
import { useLeaving } from './Presence';
import type { TableState } from './useTable';
import './hang.css';

/** The page under the head, which bar-hung surfaces are placed in. */
export function pageSize(): { w: number; h: number } {
  const main = document.querySelector('.nook-main')?.getBoundingClientRect();
  return { w: main?.width ?? window.innerWidth, h: main?.height ?? window.innerHeight };
}

export type HangKind = 'details' | 'list' | 'note';

/** What a hung surface's head spreads to move it: it opens where its host puts it, and can be
 *  pulled away from there by the head. */
const HangMove = createContext<{ handle: ReturnType<typeof useMovable>['handle']; moving: boolean } | null>(null);

export function useHangMove() {
  return useContext(HangMove);
}

/**
 * The one surface a page hangs off what was clicked: a card's details, a bar word's list, a
 * note. It is only as tall as its content; where it sits is its host's to say ([CardHang] for a
 * card, the page for a word).
 */
export function Hang({
  kind,
  label,
  className,
  style,
  tint,
  children,
  hangRef,
}: {
  kind: HangKind;
  label: string;
  className?: string;
  style?: CSSProperties;
  /** A note's own colours: the chip it is filled with and the edge around it. */
  tint?: { chip: string; edge: string };
  children: ReactNode;
  hangRef?: RefObject<HTMLElement | null>;
}) {
  const leaving = useLeaving();
  return (
    <section
      ref={hangRef}
      inert={leaving}
      className={`hang hang-${kind}${className ? ` ${className}` : ''}${leaving ? ' is-leaving' : ''}`}
      style={tint ? ({ ...style, '--note-chip': tint.chip, '--note-edge': tint.edge } as CSSProperties) : style}
      aria-label={label}
    >
      {children}
    </section>
  );
}

/** A [Hang] hung off a word of the bar, in the window: it stays with the bar while the page scrolls. */
export function BarHang({
  kind,
  label,
  className,
  place: given,
  holdPlace = false,
  width,
  height,
  children,
}: {
  kind: HangKind;
  label: string;
  className?: string;
  /** Anchored by its left edge, or by its right edge where its width is its content's. */
  place: { left?: number; right?: number; top: number; maxHeight: number };
  /** Kept where it first opened, so a panel pulled to another size grows from its top-left. */
  holdPlace?: boolean;
  width?: number;
  height?: number;
  children: ReactNode;
}) {
  const { offset, moving, handle } = useMovable();
  const opened = useRef(given);
  const place = holdPlace ? { ...opened.current, maxHeight: given.maxHeight } : given;
  return (
    <Hang
      kind={kind}
      label={label}
      className={className}
      style={{
        position: 'absolute',
        ...(place.right !== undefined ? { right: place.right - offset.x } : { left: (place.left ?? 0) + offset.x }),
        top: place.top + offset.y,
        maxHeight: place.maxHeight,
        width,
        height,
      }}
    >
      <HangMove.Provider value={{ handle, moving }}>{children}</HangMove.Provider>
    </Hang>
  );
}

/** What a press on the page lets a hung surface stand through: the bar, a card, the surface
 *  itself, and the menus and dialogs that open over it. */
const KEEPS = '.hang, .table-bar, [data-card], .plane-menu, [role="menu"], [role="dialog"], [data-hang-keeps]';

/** How far a press may travel and still be a press: further, it was a drag that moved the view. */
const GROUND_SLOP = 4;

/** A press on empty ground, which is anywhere that is none of those, lets the surface go; a drag
 *  from there, which moves the view, does not. */
export function useCloseOnGround(active: boolean, onClose: () => void): void {
  const latest = useRef(onClose);
  latest.current = onClose;
  useEffect(() => {
    if (!active) return;
    let pressed: { x: number; y: number } | null = null;
    const down = (e: PointerEvent) => {
      pressed = e.target instanceof Element && !e.target.closest(KEEPS) ? { x: e.clientX, y: e.clientY } : null;
    };
    const up = (e: PointerEvent) => {
      const at = pressed;
      pressed = null;
      if (at && Math.hypot(e.clientX - at.x, e.clientY - at.y) <= GROUND_SLOP) latest.current();
    };
    document.addEventListener('pointerdown', down);
    document.addEventListener('pointerup', up);
    return () => {
      document.removeEventListener('pointerdown', down);
      document.removeEventListener('pointerup', up);
    };
  }, [active]);
}

/**
 * A [Hang] hung off a card, placed by [hangBeside] in [findFrame]'s box (the table, or the part of
 * the page a card stands in) and drawn in [host]. [fixed] puts it in the page under the head rather
 * than in [host], for a frame that is itself fixed. A card that cannot be found has nothing to hang
 * off, and the panel waits unseen.
 */
function HangAt({
  host,
  moveKey,
  findCard,
  findFrame,
  fixed,
  kind,
  label,
  className,
  size,
  tint,
  children,
}: {
  host: HTMLElement | null;
  /** What it hangs off: a move is let go once it hangs off something else. */
  moveKey?: string | null;
  findCard: () => { getBoundingClientRect(): DOMRect } | null | undefined;
  findFrame: () => HTMLElement | null | undefined;
  fixed: boolean;
  kind: Exclude<HangKind, 'list'>;
  label: string;
  className?: string;
  /** What the panel measures, where it does not hug its content. */
  size?: { w: number; h: number };
  tint?: { chip: string; edge: string };
  children: ReactNode;
}) {
  const panel = useRef<HTMLElement>(null);
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  const { offset, moving, handle, place: moveTo } = useMovable();
  const hungOff = useRef(moveKey);
  useEffect(() => {
    // Hung off another card, it opens beside that one; a note its first word just made keeps its place.
    if (hungOff.current != null && hungOff.current !== moveKey) moveTo({ x: 0, y: 0 });
    hungOff.current = moveKey;
  }, [moveKey, moveTo]);
  const [measured, setMeasured] = useState({ w: 0, h: 0 });
  /** A panel of its own [size] is placed by the size it opened at: pulled to another, it grows
   *  from where it stands rather than being placed again. */
  const placedBy = useRef<{ key: string | null | undefined; size: { w: number; h: number } } | null>(null);
  if (size && (!placedBy.current || (placedBy.current.key != null && placedBy.current.key !== moveKey))) placedBy.current = { key: moveKey, size };
  else if (placedBy.current) placedBy.current.key = moveKey;

  useLayoutEffect(() => {
    const el = panel.current;
    if (!el) return;
    const read = () => setMeasured((was) => (was.w === el.offsetWidth && was.h === el.offsetHeight ? was : { w: el.offsetWidth, h: el.offsetHeight }));
    read();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  });

  useLayoutEffect(() => {
    const card = findCard();
    const frame = findFrame();
    if (!card || !frame || !host) return setAt(null);
    const f = frame.getBoundingClientRect();
    const c = card.getBoundingClientRect();
    const rel = { left: c.left - f.left, top: c.top - f.top, right: c.right - f.left, bottom: c.bottom - f.top };
    const place = hangBeside(rel, placedBy.current?.size ?? measured, { w: f.width, h: f.height }, kind === 'note' && !fixed ? 10 : 0);
    const origin = (fixed ? host.closest('.nook-main') : host)?.getBoundingClientRect() ?? f;
    const next = { left: f.left - origin.left + place.left, top: f.top - origin.top + place.top };
    setAt((was) => (was && was.left === next.left && was.top === next.top ? was : next));
  });

  if (!host) return null;
  return createPortal(
    <Hang
      kind={kind}
      label={label}
      className={className}
      tint={tint}
      hangRef={panel}
      style={{ position: fixed ? 'fixed' : 'absolute', left: (at?.left ?? 0) + offset.x, top: (at?.top ?? 0) + offset.y, ...(at ? null : { opacity: 0, pointerEvents: 'none' as const }), ...(size ? { width: size.w, height: kind === 'note' ? size.h : undefined } : null) }}
    >
      <HangMove.Provider value={{ handle, moving }}>{children}</HangMove.Provider>
    </Hang>,
    host,
  );
}

/**
 * A [Hang] hung off a card on the table, in the table's own box so it scrolls with its card and
 * passes under the bar. In full canvas the table is the page under the head, and the panel is
 * placed in the page the same way. A card that is not on the table has nothing to hang off: the
 * caller hangs it from where it was opened instead.
 */
export function CardHang({
  table,
  surface,
  cardKey,
  fallback,
  ...rest
}: {
  table: TableState;
  surface: RefObject<HTMLElement | null>;
  cardKey: string | null;
  /** Where its card will stand, a canvas point and the card's size, while there is no card: the
   *  panel hangs off that, and moves to the card once it stands, without being drawn again. */
  fallback?: { at: { x: number; y: number }; card: { w: number; h: number } } | null;
  kind: Exclude<HangKind, 'list'>;
  label: string;
  className?: string;
  size?: { w: number; h: number };
  tint?: { chip: string; edge: string };
  children: ReactNode;
}) {
  const host = table.slot.current;
  return (
    <HangAt
      host={host}
      moveKey={cardKey}
      fixed={table.full}
      findCard={() => {
        const card = cardKey ? [...(surface.current?.querySelectorAll<HTMLElement>('[data-card-key]') ?? [])].find((e) => e.dataset['cardKey'] === cardKey) : null;
        if (card || !fallback) return card;
        const f = host?.querySelector<HTMLElement>('.table')?.getBoundingClientRect();
        if (!f) return null;
        const { x, y, zoom } = table.cam.camera;
        const rect = new DOMRect(f.left + x + fallback.at.x * zoom, f.top + y + fallback.at.y * zoom, fallback.card.w * zoom, fallback.card.h * zoom);
        return { getBoundingClientRect: () => rect };
      }}
      findFrame={() => host?.querySelector<HTMLElement>('.table')}
      {...rest}
    />
  );
}
